'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { ChevronRight, Shield, X, Flag, Repeat, ArrowLeftCircle } from 'lucide-react';
import { useTypewriter } from '@/components/tutorial/shared';
import { ViewModePick } from '@/features/lobby/onboarding/ViewModePick';
import {
  ensureAudioReady,
  playTutorialTypeSound,
} from '@/lib/sounds';
import {
  ensureHqAudioReady,
  hqPlayBuyout,
  hqPlaySeatClick,
} from '@/features/lobby/audio/hq-sounds';
import {
  BUYOUT_TILE_VISIBLE_AT,
  BUYOUT_TOTAL_MS,
} from '@/features/lobby/board-canvas/animation-timings';
import { useGraphics } from '@/features/lobby/settings/GraphicsContext';
import { useTutorial } from './TutorialContext';
import {
  TUTORIAL_VIEWER,
  TUTORIAL_RAIDER,
  TUTORIAL_FLOOR_INDEX,
  TUTORIAL_VIEWER_PRICE,
  TUTORIAL_VIEWER_PRICE_USD,
  TUTORIAL_DEFEND_PRICE,
  TUTORIAL_COMPETITOR_INDEXES,
  TUTORIAL_DEFEND_WAVE_INDEXES,
  TUTORIAL_EXIT_WAVE_INDEXES,
  makeTutorialRewardEvent,
} from './tutorial-board';
import { generateGridAvatarUrl } from '@/lib/avatar';
import { useTutorialInputGuard } from './tutorial-input-guard';
import type { DialoguePosition, TutorialStepId } from './tutorial-script';

/** Result of a highlight lookup — includes both the DOM element and a
 *  flag indicating whether the lookup hit the canonical
 *  `data-tutorial-target` marker (3D path) or fell back to a 2D seat
 *  button. The caller uses the flag to decide whether to render an
 *  extra 2D blue rounded-square ring over the tile (3D already paints
 *  its own 3D blue ring in-canvas, drawing a second 2D outline on top
 *  reads as a "duplicate highlight" — user feedback). */
interface HighlightLookup {
  el: HTMLElement | null;
  /** True when the only DOM hit was the 2D seat button (i.e. Lite mode
   *  has no 3D ring tracker rendering its own highlight). */
  is2DTileFallback: boolean;
  /** True when the resolved element is a mobile chip-rail pill (the
   *  first-branch hit on `data-tutorial-target="<panel>-pill"`).
   *  Used by `OverlayShell` to suppress the blue rounded-outline ring
   *  on pill targets while still letting the dim cutout spotlight the
   *  pill — the pill's existing border + amber active state is its
   *  own affordance, the additional blue ring read as visual noise.
   *  Desktop falls through to the `-pill` suffix-strip branch (which
   *  resolves to the floating HUD panel via `data-hud-id`); that path
   *  keeps the blue ring because the panel has no built-in
   *  highlighted-state styling. */
  isPillChip: boolean;
}

/** True when the element exists in the DOM AND has non-zero layout
 *  dimensions. `getBoundingClientRect()` returns `{0,0,0,0}` for any
 *  ancestor with `display: none` (Tailwind's `hidden`, `lg:hidden`,
 *  …) — so the chip rail markers (`data-tutorial-target="yield-pill"`)
 *  on a desktop viewport are queryable but produce a zero rect. The
 *  resolver below uses this guard to skip past hidden candidates and
 *  land on the visible breakpoint's panel instead. */
function isVisibleElement(el: HTMLElement | null): boolean {
  if (!el || !el.isConnected) return false;
  const rect = el.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

/** When the matched HUD wrapper contains a `[data-draggable-root]`
 *  child, prefer the child for measurement.
 *
 *  The floating HUD panels (`yield`, `holders`, `mytiles`, `rewards`,
 *  `skybox`, …) are rendered as:
 *
 *    <motion.div data-hud-id="…">           // AnimatePresence wrapper
 *      <motion.div data-draggable-root="…"  // DraggableFloatingPanel
 *                  style={{ x, y }}>        //   ← persisted drag offset
 *        <RewardsPanel />                   // visible chrome
 *      </motion.div>
 *    </motion.div>
 *
 *  The user-draggable inner div translates via a CSS transform when
 *  the panel has been moved (positions persist to localStorage per
 *  `id`). CSS transforms don't reflow the parent's layout box, so
 *  `outer.getBoundingClientRect()` returns the *original* panel slot
 *  — the spotlight then paints around an empty patch of the HUD layer
 *  while the visible panel sits somewhere else (the user described
 *  this as "blue highlight misses the activity panel considerably").
 *
 *  Walking down to the inner `[data-draggable-root]` produces the
 *  rect of the actually-rendered panel because the transform is
 *  applied to that element directly — so its bounding rect is the
 *  visible rect. Fresh-mount (x=y=0) lands on the same coordinates
 *  as the outer wrapper, so this is a no-op for users who haven't
 *  customised their HUD layout — it only matters once a panel has
 *  been dragged. */
function preferDraggableRoot(el: HTMLElement | null): HTMLElement | null {
  if (!el) return null;
  const inner = el.querySelector<HTMLElement>('[data-draggable-root]');
  if (inner && isVisibleElement(inner)) return inner;
  return el;
}

/** Tags a DOM element via `data-tutorial-target` and `[data-hud-id]` lookups.
 *
 *  `*-pill` suffix handling: panel-pill targets (`yield-pill`,
 *  `rewards-pill`, `chat-pill`) live on the mobile chip rail
 *  (`MobilePanelChipRail`). On desktop the chip rail still mounts in
 *  the DOM but is `display: none` via the parent's `lg:hidden`, so a
 *  raw `querySelector` hit returns a zero-rect element and the
 *  spotlight ends up at the viewport top-left corner. We therefore
 *  walk the candidate selectors and only accept a match whose
 *  bounding rect is non-zero — that way the desktop pass falls
 *  through to the bare panel id (`yield` / `rewards` / `chat`) and
 *  the spotlight lands on the visible floating HUD panel. Mobile is
 *  unaffected because the chip is the visible match on those
 *  breakpoints.
 */
function findHighlightElement(target: string | null | undefined): HighlightLookup {
  if (!target) return { el: null, is2DTileFallback: false, isPillChip: false };
  if (typeof document === 'undefined') {
    return { el: null, is2DTileFallback: false, isPillChip: false };
  }
  // The tutorial-injected DOM markers (e.g. the 3D floor tile tracker)
  // take precedence over generic HUD ids so they can override a panel
  // that happens to share the name. They're also the only way to land a
  // spotlight on the 3D canvas, where individual tiles have no DOM.
  const byTutorial = document.querySelector<HTMLElement>(`[data-tutorial-target="${target}"]`);
  if (isVisibleElement(byTutorial)) {
    // First-branch hit on a `<panel>-pill` marker is the mobile chip
    // rail's pill — only the chip rail tags itself with the `-pill`
    // suffix. We surface this on the lookup so `OverlayShell` can
    // skip the blue rounded-outline ring for these targets (the pill
    // already has its own visible affordance: amber border + filled
    // active background). Desktop falls through to the suffix-strip
    // branch below where `isPillChip` stays `false` because the
    // floating HUD panel has no built-in highlighted state, so the
    // blue ring is its only "look here" cue there.
    const isPillChip = target.endsWith('-pill');
    return { el: byTutorial, is2DTileFallback: false, isPillChip };
  }
  const byHud = document.querySelector<HTMLElement>(`[data-hud-id="${target}"]`);
  if (isVisibleElement(byHud)) {
    return {
      el: preferDraggableRoot(byHud),
      is2DTileFallback: false,
      isPillChip: false,
    };
  }
  // Desktop fallback for mobile-pill targets — strip the suffix and
  // retry both selectors so the visible breakpoint's panel wins. On
  // tablet (`md`–`lg`) both the chip rail AND the floating panel
  // mount, but the chip rail is the visible one (its parent is
  // `lg:hidden`); on desktop (`lg+`) only the floating panel is
  // visible. The `data-tutorial-target` re-lookup with the bare id
  // covers the mobile `MobilePanelOverlay` case (the open panel
  // overlay carries `data-tutorial-target={mobilePanel}` so the
  // tutorial step driver lands on it when a panel is auto-opened).
  if (target.endsWith('-pill')) {
    const bare = target.slice(0, -'-pill'.length);
    const byTutorialBare = document.querySelector<HTMLElement>(
      `[data-tutorial-target="${bare}"]`,
    );
    if (isVisibleElement(byTutorialBare)) {
      return { el: byTutorialBare, is2DTileFallback: false, isPillChip: false };
    }
    const byHudBare = document.querySelector<HTMLElement>(`[data-hud-id="${bare}"]`);
    if (isVisibleElement(byHudBare)) {
      return {
        el: preferDraggableRoot(byHudBare),
        is2DTileFallback: false,
        isPillChip: false,
      };
    }
  }
  if (target === 'tutorial-floor-tile') {
    // 2D fallback — `LobbySeatTile2D` tags every tile with `data-seat-index`.
    // Important: we explicitly query inside the canvas wrapper and
    // ignore the 3D-marker selector above so this branch only fires
    // in Lite mode (where no `data-tutorial-target` marker exists).
    const seatBtn = document.querySelector<HTMLElement>(
      `[data-seat-index="${TUTORIAL_FLOOR_INDEX}"]`,
    );
    if (isVisibleElement(seatBtn)) {
      return { el: seatBtn, is2DTileFallback: true, isPillChip: false };
    }
  }
  return { el: null, is2DTileFallback: false, isPillChip: false };
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
  /** True when the rect was resolved via the 2D seat-button fallback;
   *  callers paint a visible blue ring around the tile in this case so
   *  Lite mode gets the same "this is what to look at" affordance the
   *  3D in-canvas ring provides for the cinematic renderer. */
  is2DTileFallback: boolean;
  /** True when the rect was resolved via the 3D tile tracker (the
   *  invisible AABB marker `TutorialFloorTracker3D` drops over the
   *  projected tile each frame). Used by `OverlayShell` to render the
   *  spotlight cutout as a **circle** (border-radius: 50%) instead of
   *  a rounded rectangle — a circular cutout matches the in-canvas
   *  ring shape and avoids the previous "highlighted rectangle is
   *  not mapped to the 3D tile's plane" complaint. The blue 2D
   *  outline stays off (the in-canvas ring is the visible highlight),
   *  but we now *do* dim everything outside the cutout per the
   *  user's "everything should be darken except for the tile and the
   *  blue ring highlight" instruction for `find-floor`. Window-level
   *  input guard plus R3F seat-level filter still keep the click
   *  restricted to the floor seat. */
  is3DTileFallback: boolean;
  /** True when the rect was resolved via a mobile chip-rail pill
   *  marker. Plumbed through from `HighlightLookup.isPillChip` so
   *  `OverlayShell` can suppress the blue rounded-outline ring on
   *  pill targets — see the doc on `HighlightLookup.isPillChip` for
   *  the full rationale. */
  isPillChip: boolean;
}

function rectOf(target: string | null, lookup: HighlightLookup): SpotlightRect | null {
  if (!lookup.el) return null;
  const r = lookup.el.getBoundingClientRect();
  // 3D tile fallback = tile target whose DOM hit was the
  // `[data-tutorial-target]` marker dropped by `TutorialFloorTracker3D`
  // (i.e. *not* the 2D seat button — that path sets `is2DTileFallback`).
  const is3DTileFallback = isTileTarget(target) && !lookup.is2DTileFallback;
  return {
    top: r.top,
    left: r.left,
    width: r.width,
    height: r.height,
    is2DTileFallback: lookup.is2DTileFallback,
    is3DTileFallback,
    isPillChip: lookup.isPillChip,
  };
}

/** Targets that point at a board tile rather than a HUD panel/button.
 *  Tile targets get their highlight from the 3D blue ring
 *  (`TutorialFloorTracker3D`) — drawing a 2D rectangle over the same tile
 *  read as a duplicate / "blue rounded square" overlay (user feedback). */
function isTileTarget(target: string | null | undefined): boolean {
  if (!target) return false;
  return target === 'tutorial-floor-tile' || target.startsWith('tutorial-tile-');
}

/** Track the highlighted element's DOM rect so the dim mask cuts a hole over it.
 *
 * **3D tile targets** resolve to the projected-AABB marker dropped by
 * `TutorialFloorTracker3D`. The rect is flagged `is3DTileFallback` so
 * `OverlayShell` knows to render the cutout as a **circle**
 * (border-radius: 50%) — that matches the in-canvas blue ring shape
 * and avoids the previous "highlighted rectangle is not mapped to the
 * 3D tile's plane" misalignment. The corners of the AABB outside the
 * inscribed circle remain over the canvas (no blocker covers them) so
 * clicks landing in them still raycast to the floor tile correctly,
 * but they read as dim — the visual cutout is the inscribed circle.
 *
 * **2D / Lite tile targets** resolve to the seat-button rect so the
 * overlay paints the same blue rounded ring it uses for HUD targets.
 * The seat button is itself axis-aligned (no perspective), so AABB
 * === button rect and the cutout matches the tile precisely.
 */
function rectsEqual(a: SpotlightRect | null, b: SpotlightRect | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.round(a.top) === Math.round(b.top) &&
    Math.round(a.left) === Math.round(b.left) &&
    Math.round(a.width) === Math.round(b.width) &&
    Math.round(a.height) === Math.round(b.height) &&
    a.is2DTileFallback === b.is2DTileFallback &&
    a.is3DTileFallback === b.is3DTileFallback &&
    a.isPillChip === b.isPillChip
  );
}

function useSpotlightRect(target: string | null): SpotlightRect | null {
  const [rect, setRect] = useState<SpotlightRect | null>(null);
  const lastRectRef = useRef<SpotlightRect | null>(null);
  useEffect(() => {
    if (!target) {
      lastRectRef.current = null;
      setRect(null);
      return;
    }
    let raf = 0;
    let interval: ReturnType<typeof setInterval> | null = null;
    const apply = (next: SpotlightRect | null) => {
      if (rectsEqual(lastRectRef.current, next)) return;
      lastRectRef.current = next;
      setRect(next);
    };
    const update = () => {
      const lookup = findHighlightElement(target);
      apply(rectOf(target, lookup));
    };
    // Per-frame tracking so the highlight rectangle glides with the
    // tile as `BoardCanvas2D` pans/zooms during a tutorial camera
    // move. Without RAF tracking the 250 ms polling makes the rect
    // snap behind the tile during the ~1.3 s camera transition. HUD /
    // panel targets are static so the RAF loop is essentially free
    // for them — `rectsEqual` short-circuits the `setState` and
    // prevents wasted re-renders.
    const tickRaf = () => {
      update();
      raf = requestAnimationFrame(tickRaf);
    };
    raf = requestAnimationFrame(tickRaf);
    interval = setInterval(update, 250);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      cancelAnimationFrame(raf);
      if (interval) clearInterval(interval);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [target]);
  return rect;
}

const REWARD_INTERVAL_MS = 1100;

export function TutorialOverlay() {
  const tutorial = useTutorial();
  const router = useRouter();
  const {
    step,
    state,
    next,
    goto,
    mutateSeats,
    getSeats,
    pushReward,
    setHighlight,
    setBranch,
    setPrefilledPrice,
    setHudPanelsHidden,
    closeDrawer,
    restart,
    triggerTileAnim,
  } = tutorial;

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  // On mobile, pin the lobby canvas to Lite (2D) so the tutorial
  // skips the 3D vs 2D picker step entirely. `TutorialContext`'s
  // `getInitialStepId()` already lands the state machine on
  // `intro-board` for ≤ 768 px viewports, but the renderer still
  // follows the user's persisted `useGraphics()` preference — without
  // this effect a mobile user with a previously-stored `'high'` would
  // see the 3D canvas mount, then the picker get skipped, then have
  // to ride the rest of the tutorial in cinematic 3D on a phone. We
  // override the level for this provider's lifetime *without writing
  // to localStorage* (`setLevelTransient`), so the user's persisted
  // graphics preference in the surrounding `(lobby)` route group is
  // untouched: when they exit the tutorial and the route's
  // `LobbyClient` / `GraphicsProvider` remounts, it re-reads
  // localStorage and observes whatever they had picked before.
  //
  // The previous flow used `setLevel` (persistent) with no
  // compensating restore on tutorial exit, leaving any mobile
  // tutorial visitor stuck on 2D forever after — which was one of
  // the "graphics settings reset to 2D" reports we tracked down
  // alongside the lobby-navigation tile-state leak. Mount-only:
  // subsequent renders shouldn't re-pin the level if the user
  // explicitly switches mid-tutorial.
  const { level: graphicsLevel, setLevelTransient: setGraphicsLevelTransient } =
    useGraphics();
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (!isMobile) return;
    if (graphicsLevel !== 'lite') {
      setGraphicsLevelTransient('lite');
    }
    // Defensive: if the tutorial somehow lands on `view-pick` (e.g.
    // a desktop-render hydrated client that just resized into mobile,
    // or a future code path that re-routes here) advance straight to
    // the first content step. The provider's lazy initializer
    // already covers the common case at mount time.
    if (state.stepId === 'view-pick') {
      goto('intro-board');
    }
    // Mount-only: once we've pinned Lite for this run we're done. The
    // override is dropped automatically when the tutorial provider
    // unmounts; nothing persists across replays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Root of the tutorial overlay portal — the input guard uses this
  // to allow events targeted at the dialogue panel, Skip / Next /
  // branch / outro CTAs (anything inside the portal) without coupling
  // to individual button refs. `OverlayShell` attaches the ref to its
  // outermost `<div>`. Stored in component state (not a plain ref) so
  // the guard hook re-evaluates with the latest node when React first
  // commits the portal — `useEffect` running with `null` would leave
  // a render's worth of events unguarded.
  const [overlayRoot, setOverlayRoot] = useState<HTMLDivElement | null>(null);

  // Robust input guard. Runs at `window` capture phase and blocks any
  // pointer / touch / keyboard event whose target isn't inside the
  // overlay portal, the currently-highlighted element, or (for tile
  // steps) the board canvas. This is the canonical "you can only
  // interact with the spotlit thing" enforcement — the visual dim /
  // blocker rects in `OverlayShell` are now defence in depth.
  useTutorialInputGuard({
    overlayRoot,
    highlightTarget: state.highlightTarget,
    // 3D tile targets resolve to an invisible 0×0 tracker overlay
    // (`pointer-events: none`), so the actual click target is the
    // R3F `<canvas>`. Allow the canvas wrapper for tile steps; the
    // seat-level filter in `LobbyClient.openDrawerForSeat` then
    // narrows that to the floor seat only.
    allowBoardCanvas:
      state.highlightTarget === 'tutorial-floor-tile' ||
      (state.highlightTarget?.startsWith('tutorial-tile-') ?? false),
  });

  // Sync highlight target whenever the step changes.
  //
  // The 1.2× preset highlight now fires the instant `set-price`
  // becomes the active step — the user requested no delay between
  // the drawer opening and the next-action callout appearing. The
  // `useSpotlightRect` RAF tracker handles the case where the
  // preset DOM node hasn't mounted yet (the rect just stays null
  // until the chip exists, then snaps onto it as soon as the
  // drawer's slide-in finishes).
  useEffect(() => {
    setHighlight(step.highlightTarget ?? null, step.dim ?? false);
  }, [step.id, step.highlightTarget, step.dim, setHighlight]);

  const rect = useSpotlightRect(state.highlightTarget);

  // Side-effects per step on entry.
  const seqRef = useRef(0);
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;
    // Side-effect timers spawned by per-step routines (e.g. the
    // competitor wave's per-tile deferred price bump). Tracked here
    // so the cleanup pass on step change cancels every outstanding
    // mutation — without it, a Skip Tutorial mid-wave would still
    // commit price bumps after we've left the step. `window.setTimeout`
    // resolves to `number` in the browser DOM lib but `NodeJS.Timeout`
    // when @types/node is in scope; using `number` here keeps both
    // paths happy since `clearTimeout` accepts either.
    const pendingTimers = new Set<number>();

    const startRewardStream = (
      count: number,
      intervalMs = REWARD_INTERVAL_MS,
      usdSupplier?: (seq: number, idx: number) => number,
    ) => {
      let pushed = 0;
      interval = setInterval(() => {
        if (cancelled) return;
        const seq = seqRef.current++;
        // `usdSupplier` is the override hook used by the
        // competitor-buyouts burst to push $5–$25 chunks instead of
        // the ambient $0.40–$1.80 drip — see the call site below.
        const usdOverride = usdSupplier ? usdSupplier(seq, pushed) : undefined;
        const { event, usd } = makeTutorialRewardEvent(seq, usdOverride);
        pushReward(event, usd);
        pushed += 1;
        if (pushed >= count && interval) {
          clearInterval(interval);
          interval = null;
        }
      }, intervalMs);
    };

    const runCompetitorBuyouts = () => {
      // Sweep the 5 cheapest cheap-band seats listed in
      // `TUTORIAL_COMPETITOR_INDEXES`. The user asked for the wave
      // to fire as "several tiles being bought in quick succession
      // (similar to the defend route at the end)" and trimmed the
      // count from 10 → 5 ("there should be 5 buyouts not 10 in
      // both instances"); the viewer's tile narrative is preserved
      // by the matching shrink of `CHEAP_BAND_COUNT` (14 → 9), so
      // sweeping 5 of the 9 cheaper tiles still leaves the viewer
      // at sorted rank 4 (position 5) — "approaching the floor"
      // before the defend/exit choice card lands.
      //
      // Per-tile choreography (mirrors the take-over flow so every
      // buyout in the tutorial reads the same way):
      //
      //   t=0     crumble + sound start, holder flips, *price held*
      //   t=~1.2s slab regrown → bump price, tile slides to new rank
      //   t=~1.7s crumble + slide done
      //
      // Holding the price during the crumble keeps each tile pinned
      // at its old sort slot while the shatter plays — without it,
      // the tile would lerp to its new position mid-crumble and the
      // user can't tell which slab is breaking.
      //
      // Pricing math (loaded into the new-price computation below)
      // is what lands the viewer at sorted rank 4 (position 5)
      // after the wave AND keeps the defend-side math clean:
      //   - Buyouts 0..3 (the cheaper 4 indexes) get a 1.3× bump.
      //     Original prices in [$5.10, $5.50] → bumped to roughly
      //     [$6.63, $7.15] — all above the viewer's $6 listing,
      //     all below the viewer's $12 defend price.
      //   - Buyout 4 (the 5th, last) gets a hardcoded ~$13.50
      //     bump (well above the $12 defend price). This is the
      //     load-bearing trick that makes the post-defend rank
      //     come out to exactly 8 cheaper tiles — and therefore
      //     position 9 — instead of 9 cheaper / position 10. The
      //     visual is just "one of these new owners overpaid"; the
      //     user can't tell the price is hand-tuned.
      // After the wave: 4 cheap-band survivors (indexes 6..9) +
      // 4 wave-1 bumped under $12 + 1 wave-1 bumped above $12 =
      // viewer at rank 4 (position 5).
      const indexes = [...TUTORIAL_COMPETITOR_INDEXES];
      // Each raider gets its own deterministic address + matching
      // generated grid avatar so each new owner reads as a distinct
      // player taking the seat. Roster size matches the wave size
      // (5) so no avatar repeats in a single sweep.
      const RAIDER_ROSTER: Array<{
        address: string;
        name: string;
        avatarUrl: string;
        level: number;
      }> = [
        { address: TUTORIAL_RAIDER.address, name: TUTORIAL_RAIDER.name, avatarUrl: TUTORIAL_RAIDER.avatarUrl, level: 4 },
        { address: '0xC0FFEE0B100000000000000000000000000000B2', name: 'CoolCat', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000B2', 96), level: 5 },
        { address: '0xC0FFEE0B100000000000000000000000000000C3', name: 'DragonLord', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000C3', 96), level: 4 },
        { address: '0xC0FFEE0B100000000000000000000000000000D4', name: 'Wizard', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000D4', 96), level: 6 },
        { address: '0xC0FFEE0B100000000000000000000000000000E5', name: 'Devil', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000E5', 96), level: 5 },
      ];
      const STAGGER_MS = 500;
      // `13.50 USDC` written as a bigint (13_500_000) keeps the
      // wave math out of float territory.
      const ABOVE_DEFEND_PRICE = 13_500_000n;
      // Pre-warm the WebAudio context once before the cascade so
      // the very first crumble's SFX doesn't stutter — same trick
      // the defend wave uses.
      ensureHqAudioReady();
      indexes.forEach((seatIndex, i) => {
        const t = window.setTimeout(() => {
          if (cancelled) return;
          // Synchronous read via the seats ref. Capturing the
          // snapshot inside each tick (vs. up-front) means the
          // holder we crumble out matches whatever's actually on
          // the board the moment that tile shatters — robust
          // against intervening reducer dispatches.
          const snapshot = getSeats()[seatIndex];
          if (!snapshot) return;
          const raider = RAIDER_ROSTER[i % RAIDER_ROSTER.length];
          // Last buyout in the wave gets the hardcoded above-$12
          // bump described in the comment above. Everyone else
          // gets the standard 1.3× bump (price + 30%).
          const isAboveDefendBump = i === indexes.length - 1;
          const newPrice = isAboveDefendBump
            ? ABOVE_DEFEND_PRICE
            : snapshot.selfAssessedPrice + (snapshot.selfAssessedPrice * 3n) / 10n;
          // Fire production buyout anim + SFX on the same tick.
          triggerTileAnim(seatIndex, {
            kind: 'buyout',
            startedAt: performance.now(),
            previousHolder: {
              address: snapshot.holder,
              avatarUrl: snapshot.holderAvatar ?? null,
              level: snapshot.holderLevel ?? 0,
            },
            newHolder: {
              address: raider.address,
              avatarUrl: raider.avatarUrl,
              level: raider.level,
            },
          });
          hqPlayBuyout();
          // Holder-only flip immediately (so the regrown slab
          // paints the new avatar). Price stays the same → seat
          // sits in its pre-buyout sort slot during the crumble.
          mutateSeats((prev) => {
            const next = [...prev];
            const seat = next[seatIndex];
            if (seat) {
              next[seatIndex] = {
                ...seat,
                holder: raider.address,
                holderName: raider.name,
                holderAvatar: raider.avatarUrl,
                holderLevel: raider.level,
                isOwn: false,
              };
            }
            return next;
          });
          // Deferred price bump → re-sort → smooth slide once
          // the slab is back. Same staging as the take-over
          // bridge.
          const priceTimer = window.setTimeout(() => {
            if (cancelled) return;
            mutateSeats((prev) => {
              const next = [...prev];
              const seat = next[seatIndex];
              if (seat) {
                next[seatIndex] = {
                  ...seat,
                  selfAssessedPrice: newPrice,
                  balance: newPrice * 4n,
                  lastPriceChangeTime: Math.floor(Date.now() / 1000),
                };
              }
              return next;
            });
          }, BUYOUT_TILE_VISIBLE_AT);
          pendingTimers.add(priceTimer);
        }, i * STAGGER_MS);
        pendingTimers.add(t);
      });
    };

    if (step.id === 'rewards-stream') {
      startRewardStream(6, 900);
    }
    if (step.id === 'competitor-buyouts') {
      // Hide the lobby HUD panels immediately so the 5 crumbles
      // read against an uncluttered board. Per the latest script
      // ("On the time of buyouts+rewards accruing just show the
      // buyouts"), the panels return after the wave finishes —
      // restoring just before `defend-or-leave` shows the user the
      // cumulative impact (rewards spike, mytiles refresh) the
      // moment the panels reappear.
      setHudPanelsHidden(true);
      // Wait for the camera to finish pulling back to its medium-
      // wide framing before the first competitor crumble fires.
      // The pose tween for `competitor-buyouts` is 1500 ms, so a
      // 1500 ms pre-roll lines the first shatter up with the
      // camera arriving at its new framing — the user sees the
      // camera move first, *then* the buyout wave starts, just
      // like the take-over flow.
      const startTimer = window.setTimeout(() => {
        if (cancelled) return;
        runCompetitorBuyouts();
      }, 1500);
      pendingTimers.add(startTimer);
      // "The board is heating up" — the dramatic reward burst is
      // still 10 rewards in quick succession ($5-$25), the user
      // didn't ask to shrink the rewards stream when they reduced
      // the buyouts. Even though the rewards panel is HIDDEN
      // during the wave, the events still accumulate in state so
      // the panel's total visibly jumps when it restores at the
      // end of the wave (the impact of the buyouts becomes legible
      // *after* the spectacle, rather than competing with it).
      // The deterministic 5/9/13/17/21/25/22/18/14/9 ladder peaks
      // mid-burst (~$25) and tapers either side. 350 ms interval
      // × 10 rewards = ~3.5 s, comfortably inside the (now-tighter)
      // ~7.2 s step window even with the camera pre-roll out front.
      const REWARD_BURST_USDS = [5, 9, 13, 17, 21, 25, 22, 18, 14, 9];
      const rewardsStartTimer = window.setTimeout(() => {
        if (cancelled) return;
        startRewardStream(
          REWARD_BURST_USDS.length,
          350,
          (_seq, idx) => REWARD_BURST_USDS[idx % REWARD_BURST_USDS.length],
        );
      }, 1800);
      pendingTimers.add(rewardsStartTimer);
      // Restore panels right after the LAST crumble finishes
      // shattering. With 5 buyouts staggered at 500 ms, the last
      // one fires at t = 1500 ms (camera pre-roll) + 2000 ms
      // (i=4 stagger) = 3500 ms after step entry. The shatter +
      // regrow + slide tail runs another ~1740 ms
      // (BUYOUT_TOTAL_MS), so the panels can come back at ~5300 ms.
      // The step auto-advances at 7240 ms, leaving the user a
      // ~2 s window with the panels back up before the
      // defend-or-leave choice card lands — long enough to clock
      // the cumulative reward total but short enough that the
      // wave still feels like the "main event".
      const restoreTimer = window.setTimeout(() => {
        if (cancelled) return;
        setHudPanelsHidden(false);
      }, 5300);
      pendingTimers.add(restoreTimer);
    }
    if (step.id === 'set-price') {
      setPrefilledPrice(TUTORIAL_VIEWER_PRICE_USD);
    }
    // `open-buyout` is gone — the drawer is now opened by the user
    // tapping the highlighted floor tile during `find-floor`. The
    // bridge in `LobbyClient.openDrawerForSeat` routes the click
    // through `tutorial.openDrawer`, the script then advances to
    // `set-price` (see effect below).
    // `own-tile-blue` no longer mutates the seat — the take-over
    // bridge in `TutorialProviders.handleQueueComplete` owns the
    // staged crumble + holder flip + deferred price bump so the
    // animation choreography is centralised. Re-setting the seat
    // here would cancel the deferred slide.
    if (step.id === 'path-leave') {
      // Per the latest script the exit path is a 5-tile cascade:
      // "we show 5 quick buyouts that culminates in the user's
      // tile being bought and the congratulations screen
      // appearing". Coming into this step the viewer sits at
      // sorted rank 4 (position 5) thanks to the competitor-
      // buyouts wave, so the 4 cheapest tiles in front of them are
      // the cheap-band survivors at indexes 6..9 (the new
      // 9-wide cheap band — see `TUTORIAL_EXIT_WAVE_INDEXES`).
      // The wave crumbles those four in succession and lands the
      // 5th shatter on the viewer's own tile (index 0) — the
      // visual "the floor walked all the way up to me" payoff.
      //
      // Each buyout still fires `triggerTileAnim` + `hqPlayBuyout`
      // + the holder flip on the same tick *for that tile*, so the
      // crumble, sound, and avatar swap stay in lockstep per seat.
      // Unlike `competitor-buyouts` we deliberately *don't* bump
      // prices on the 4 cheap-band survivors — the camera is
      // locked on the viewer's tile (`followViewer: false`) and
      // re-sorting the surrounding tiles mid-shatter would slide
      // them off-camera before the user gets to read the new
      // owner. The viewer's own tile gets a deferred price bump
      // (the new owner's "raised the price" signal) just like the
      // take-over bridge, but the outro fires before that bump
      // matters visually anyway.
      const PRE_ROLL_MS = 1000;
      const STAGGER_MS = 500;
      const indexes = [...TUTORIAL_EXIT_WAVE_INDEXES];
      // Distinct roster so the 5 new owners read as different
      // players (the wave-1 cascade and this exit cascade share
      // the same `CoolCat`/`DragonLord`/etc. faces — feels like
      // the same crew sweeping the board, which is the desired
      // narrative).
      const EXIT_ROSTER: Array<{
        address: string;
        name: string;
        avatarUrl: string;
        level: number;
      }> = [
        { address: '0xC0FFEE0B100000000000000000000000000000B2', name: 'CoolCat', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000B2', 96), level: 5 },
        { address: '0xC0FFEE0B100000000000000000000000000000C3', name: 'DragonLord', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000C3', 96), level: 4 },
        { address: '0xC0FFEE0B100000000000000000000000000000D4', name: 'Wizard', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000D4', 96), level: 6 },
        { address: '0xC0FFEE0B100000000000000000000000000000E5', name: 'Devil', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000E5', 96), level: 5 },
        { address: '0xC0FFEE0B100000000000000000000000000000F6', name: 'Skull', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000F6', 96), level: 4 },
      ];
      ensureHqAudioReady();
      indexes.forEach((seatIndex, i) => {
        const t = window.setTimeout(() => {
          if (cancelled) return;
          const snapshot = getSeats()[seatIndex];
          if (!snapshot) return;
          const raider = EXIT_ROSTER[i % EXIT_ROSTER.length];
          // Whether this is the viewer's own tile (the climax —
          // last buyout in the wave). Used below to decide if we
          // also schedule the deferred price bump (only for the
          // viewer's tile; the surrounding survivors' prices are
          // held fixed to keep the camera anchor stable).
          const isViewerTile = seatIndex === TUTORIAL_FLOOR_INDEX;
          triggerTileAnim(seatIndex, {
            kind: 'buyout',
            startedAt: performance.now(),
            previousHolder: {
              address: snapshot.holder,
              avatarUrl: snapshot.holderAvatar ?? null,
              level: snapshot.holderLevel ?? 0,
            },
            newHolder: {
              address: raider.address,
              avatarUrl: raider.avatarUrl,
              level: raider.level,
            },
          });
          hqPlayBuyout();
          mutateSeats((prev) => {
            const next = [...prev];
            const seat = next[seatIndex];
            if (seat) {
              next[seatIndex] = {
                ...seat,
                holder: raider.address,
                holderName: raider.name,
                holderAvatar: raider.avatarUrl,
                holderLevel: raider.level,
                isOwn: false,
                lastPriceChangeTime: Math.floor(Date.now() / 1000),
              };
            }
            return next;
          });
          // Deliberately *no* deferred price bump on any of the 5
          // tiles — including the viewer's own. The camera is
          // locked on the viewer's original tile coordinates
          // (`followViewer: false`), so re-sorting any of these
          // seats mid-cascade would slide them off-camera the
          // moment their slab finished regrowing. We want the
          // user to read all 5 crumbles in place, with the 5th
          // (their own tile) landing as the climax just before
          // the outro takes over. The new owner's price bump is
          // a narrative beat we drop here in service of camera
          // stability — the outro lands ≈1 s after the 5th
          // crumble finishes anyway.
          // `isViewerTile` is referenced only to communicate
          // intent — we keep it on the closure so future tweaks
          // (e.g. "play a different SFX on the viewer's own
          // crumble") have a clean hook without rewriting the
          // dispatch logic.
          void isViewerTile;
        }, PRE_ROLL_MS + i * STAGGER_MS);
        pendingTimers.add(t);
      });
    }
    if (step.id === 'path-defend') {
      // Defend choreography (5-tile wave, down from the previous 10):
      //   1. Wait for the camera to settle on the viewer's tile
      //      (path-defend pose tweens for ~1100 ms) plus a 200 ms
      //      beat so motion reads as "camera arrives → world reacts"
      //      rather than two beats stacked on top of each other.
      //   2. Bump the viewer's price to the defend tier (their
      //      price climb is the *defend* gesture — the core action
      //      that makes their tile harder to take over). Coming
      //      out of `competitor-buyouts` the viewer sits at sorted
      //      rank 4 (position 5); jumping to $12 has them
      //      re-sorted to rank 8 (position 9). The 8 cheaper
      //      tiles are: 4 cheap-band survivors (indexes 6..9) + 4
      //      wave-1 bumped under $12 (indexes 1..4). The 5th
      //      wave-1 bump (index 5) is hardcoded above $12 by
      //      `runCompetitorBuyouts` specifically to make this
      //      rank math work.
      //   3. Run a *staggered* wave of 5 buyouts on the 5 next-
      //      cheapest tiles (the 4 cheap-band survivors + 1
      //      wave-1 leftover) — one every 500 ms across 2 s —
      //      then hold for 2 s of silence so the user can absorb
      //      what just happened. Each buyout's price gets bumped
      //      *above* the viewer's $12 defend price so the tile
      //      re-sorts out of the cheaper-than-viewer set, walking
      //      the viewer back from rank 8 → rank 3 (position 4) —
      //      "they end up in 4th and it looks close".
      //
      // Each buyout still fires `triggerTileAnim` + `hqPlayBuyout`
      // + the holder flip on the same tick *for that tile*, so the
      // crumble, sound, and avatar swap stay in lockstep per seat.
      // The price bump is *deferred* to after the slab regrows
      // (same staging as the take-over bridge / `runCompetitor-
      // Buyouts`) so the tile crumbles in its old slot and slides
      // to its new (higher) slot once the regrow completes — the
      // viewer's slab stays anchored as the surrounding tiles
      // shuffle around it.
      const PRE_ROLL_MS = 1300;
      // Spacing between consecutive crumbles in the wave. 5
      // buyouts × 500 ms = 2 500 ms timeline (last fires at
      // t = 2 000 ms relative to wave start).
      const STAGGER_MS = 500;
      // The 5 next-cheapest tiles after `competitor-buyouts` —
      // see `TUTORIAL_DEFEND_WAVE_INDEXES` for the rationale. We
      // pull the indexes from `tutorial-board` so the price-
      // ladder design (cheap-band size, wave-1 special bump, etc.)
      // and the wave targets stay in sync.
      const MASS_BUYOUT_INDEXES = [...TUTORIAL_DEFEND_WAVE_INDEXES];
      // Each defend-wave target gets bumped above the viewer's
      // $12 defend price so it re-sorts out of the cheaper-than-
      // viewer set. We hand the new price down per-tile (scaled
      // off the index in the wave) so the 5 resulting prices read
      // as a varied band of high-value buyouts ($13.00 → $13.80)
      // rather than 5 identical $13 listings.
      const ABOVE_DEFEND_BASE_USDC = 13_000_000n; // $13.00 in 6dp USDC
      const ABOVE_DEFEND_STEP_USDC = 200_000n;    // +$0.20 per index
      // Roster mirrors `runCompetitorBuyouts` so the new owners
      // read as distinct players. Roster size matches the wave
      // size (5) so no avatar repeats inside this wave.
      const MASS_BUYOUT_ROSTER: Array<{
        address: string;
        name: string;
        avatarUrl: string;
        level: number;
      }> = [
        { address: '0xC0FFEE0B100000000000000000000000000000F6', name: 'Skull', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B100000000000000000000000000000F6', 96), level: 4 },
        { address: '0xC0FFEE0B1000000000000000000000000000A007', name: 'Punk', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B1000000000000000000000000000A007', 96), level: 5 },
        { address: '0xC0FFEE0B1000000000000000000000000000B108', name: 'Mage', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B1000000000000000000000000000B108', 96), level: 6 },
        { address: '0xC0FFEE0B1000000000000000000000000000C209', name: 'Knight', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B1000000000000000000000000000C209', 96), level: 4 },
        { address: '0xC0FFEE0B1000000000000000000000000000D30A', name: 'Ranger', avatarUrl: generateGridAvatarUrl('0xC0FFEE0B1000000000000000000000000000D30A', 96), level: 5 },
      ];
      timer = setTimeout(() => {
        if (cancelled) return;
        // Bump the viewer's price first — the defend gesture lands
        // on the same tick as the camera settles, before the wave
        // of competitor buyouts begins. Splitting this off from
        // the holder flips lets `mutateSeats` commit the price
        // change without forcing the whole grid to re-sort against
        // 5 simultaneous holder swaps (which would jolt the
        // viewer's slab off-camera).
        mutateSeats((prev) => {
          const next = [...prev];
          const viewerSeat = next[TUTORIAL_FLOOR_INDEX];
          if (viewerSeat) {
            next[TUTORIAL_FLOOR_INDEX] = {
              ...viewerSeat,
              selfAssessedPrice: TUTORIAL_DEFEND_PRICE,
              balance: TUTORIAL_DEFEND_PRICE * 4n,
              lastPriceChangeTime: Math.floor(Date.now() / 1000),
            };
          }
          return next;
        });
        // Pre-warm the WebAudio context once before the cascade
        // so the very first crumble's SFX doesn't stutter.
        ensureHqAudioReady();
        // Schedule the 5 staggered crumbles. Each tick handles
        // one tile end-to-end: visual crumble (`triggerTileAnim`),
        // SFX (`hqPlayBuyout`), and holder swap (`mutateSeats`).
        // Re-resolving the seat snapshot inside each tick (vs.
        // capturing all 5 up-front) means the holder we crumble
        // out matches whatever's actually on the board at the
        // moment that tile crumbles — robust against any
        // intervening reducer dispatches (none expected, but
        // cheap insurance).
        MASS_BUYOUT_INDEXES.forEach((seatIndex, i) => {
          const t = window.setTimeout(() => {
            if (cancelled) return;
            const currentSeat = getSeats()[seatIndex];
            if (!currentSeat) return;
            const raider = MASS_BUYOUT_ROSTER[i % MASS_BUYOUT_ROSTER.length];
            // Above-defend bump for THIS tile. Spread across
            // [$13.00, $13.80] so the 5 new prices read as a
            // varied band rather than 5 identical $13 tiles.
            const newPrice = ABOVE_DEFEND_BASE_USDC + ABOVE_DEFEND_STEP_USDC * BigInt(i);
            triggerTileAnim(seatIndex, {
              kind: 'buyout',
              startedAt: performance.now(),
              previousHolder: {
                address: currentSeat.holder,
                avatarUrl: currentSeat.holderAvatar ?? null,
                level: currentSeat.holderLevel ?? 0,
              },
              newHolder: {
                address: raider.address,
                avatarUrl: raider.avatarUrl,
                level: raider.level,
              },
            });
            hqPlayBuyout();
            // Holder-only flip immediately so the regrown slab
            // paints the new avatar; price held during the
            // crumble so the seat stays in its old sort slot.
            mutateSeats((prev) => {
              const seat = prev[seatIndex];
              if (!seat) return prev;
              const next = [...prev];
              next[seatIndex] = {
                ...seat,
                holder: raider.address,
                holderName: raider.name,
                holderAvatar: raider.avatarUrl,
                holderLevel: raider.level,
                isOwn: false,
                lastPriceChangeTime: Math.floor(Date.now() / 1000),
              };
              return next;
            });
            // Deferred price bump → the bumped tile slides to its
            // new (above-$12) sort slot once the slab regrows.
            // Same staging as the take-over bridge / wave-1, so
            // the camera anchor (the viewer's tile) stays stable
            // while the surrounding tiles shuffle out.
            const priceTimer = window.setTimeout(() => {
              if (cancelled) return;
              mutateSeats((prev) => {
                const next = [...prev];
                const seat = next[seatIndex];
                if (seat) {
                  next[seatIndex] = {
                    ...seat,
                    selfAssessedPrice: newPrice,
                    balance: newPrice * 4n,
                  };
                }
                return next;
              });
            }, BUYOUT_TILE_VISIBLE_AT);
            pendingTimers.add(priceTimer);
          }, i * STAGGER_MS);
          pendingTimers.add(t);
        });
      }, PRE_ROLL_MS);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (interval) clearInterval(interval);
      pendingTimers.forEach((t) => window.clearTimeout(t));
      pendingTimers.clear();
      // Defensive panel restore. The `competitor-buyouts` effect
      // hides the HUD on entry and schedules a `setHudPanelsHidden(false)`
      // ~8.8 s in. If the user skips / restarts / branches before
      // that timer fires the cleanup needs to put the HUD back —
      // otherwise the next step lands on a blank board chrome.
      // Calling it unconditionally is cheap (`useReducer` short-
      // circuits a no-op state change) and saves us from forking
      // the cleanup per step id.
      setHudPanelsHidden(false);
    };
    // The handler effects are scoped to step.id transitions; the helpers
    // are stable references from `useTutorial()`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step.id]);

  // Auto-advance for the few steps that are time-bound rather than user-driven.
  //
  // `path-leave` and `path-defend` are watch-the-action interludes
  // that need to skip *past* the other branch and land directly on
  // the outro — the script lays them out linearly for readability,
  // so a plain `next()` would deposit a leave-path user on the
  // defend interlude. Routing both branches to `goto('outro')`
  // keeps the linear layout while letting the runtime jump.
  useEffect(() => {
    if (step.advance !== 'auto' || !step.autoAdvanceMs) return;
    const t = setTimeout(() => {
      if (step.id === 'path-leave' || step.id === 'path-defend') {
        goto('outro');
      } else {
        next();
      }
    }, step.autoAdvanceMs);
    return () => clearTimeout(t);
  }, [step.id, step.advance, step.autoAdvanceMs, next, goto]);

  // The user opens the buyout drawer themselves by tapping the
  // highlighted floor tile during `find-floor` — `LobbyClient`'s
  // `openDrawerForSeat` routes through `tutorial.openDrawer` so the
  // script sees `state.drawer.kind === 'buyout'`. The moment the
  // drawer is open we advance straight to `set-price` so the 1.2×
  // preset spotlight appears as the drawer slides in — no more
  // "drawer arrived but the highlight is hiding behind a 1.8 s
  // dwell" gap the user flagged.
  //
  // The 250 ms delay is just enough for Radix to start animating the
  // drawer in (so the spotlight rect updates against an in-frame
  // panel rather than a half-rendered one). Any longer and the user
  // sees the drawer fully open with no callout.
  useEffect(() => {
    if (step.id !== 'find-floor') return;
    if (state.drawer.kind !== 'buyout') return;
    const t = setTimeout(() => goto('set-price'), 250);
    return () => clearTimeout(t);
  }, [step.id, state.drawer.kind, goto]);

  // `set-price` advances when the user taps the spotlit 1.2x preset
  // chip in `PricePresets`. The chip is auto-selected by the price
  // presets default logic so the buyout summary already shows the
  // right number — the click is a "confirm" gesture so the user
  // knows *which* multiplier they picked. Listening at the document
  // level lets us watch the click without coupling `PricePresets`
  // (a generic component) to tutorial state.
  //
  // After the click we go to `set-price-rent` (the new "rent follows
  // your price" beat) before letting the user fire the actual buyout
  // on `confirm-tx`.
  useEffect(() => {
    if (step.id !== 'set-price') return;
    if (typeof document === 'undefined') return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const preset = target.closest<HTMLElement>(
        '[data-tutorial-target="tutorial-price-preset-1.2x"]',
      );
      if (!preset) return;
      goto('set-price-rent');
    };
    // `capture` so we run before the preset's own click handler can
    // unmount or re-render the spotlight target before we read it.
    document.addEventListener('click', onDocClick, true);
    return () => document.removeEventListener('click', onDocClick, true);
  }, [step.id, goto]);

  // Belt-and-braces auto-advance for confirm-tx. The simulated tx
  // queue's `onComplete` (in `TutorialQueueBridge`) is the primary
  // path — it both closes the drawer and advances to `own-tile-blue`.
  // This effect only fires if the seat ends up owned by the viewer
  // while we're still parked on `confirm-tx` (e.g. the bridge call
  // raced with a render flush), so it's a safety net rather than the
  // canonical advance.
  useEffect(() => {
    if (step.id !== 'confirm-tx') return;
    if (!tutorial.viewerOwnsSeat) return;
    goto('own-tile-blue');
    closeDrawer();
  }, [step.id, tutorial.viewerOwnsSeat, goto, closeDrawer]);

  // Skip → "Are you sure you want to leave?" dialog. The button
  // used to fast-forward straight to the celebration outro, but
  // that conflated "I quit" with "I won": the same triumphant
  // headline + CTAs landed for both, which read as misleading
  // for a bail-out. The confirm panel (rendered as a celebration-
  // style hero card below) re-asks the user with the two options
  // they spec'd — "Take me to the lobby" and "Play again". There
  // is deliberately no inline cancel: the buttons themselves are
  // the recovery — "Play again" puts the user back at the start
  // if they hit Skip by mistake. The outro celebration screen is
  // now reserved for *completed* runs (defend / leave branch
  // outros) so reaching it is always a win.
  const [skipConfirm, setSkipConfirm] = useState(false);
  const handleSkip = useCallback(() => {
    ensureAudioReady();
    ensureHqAudioReady();
    hqPlaySeatClick();
    setSkipConfirm(true);
  }, []);

  // Both branch handlers are now fire-and-forget: the path step
  // itself auto-plays the relevant side-effect (price bump for
  // defend, buyout crumble for leave) and then auto-advances to the
  // outro. We deliberately *don't* open the manage drawer here any
  // more — the watch-the-action interlude shouldn't ask the user to
  // do anything else; it's a victory lap, not another interaction.
  const handleBranchDefend = useCallback(() => {
    setBranch('defend');
    closeDrawer();
    goto('path-defend');
  }, [setBranch, closeDrawer, goto]);

  const handleBranchLeave = useCallback(() => {
    setBranch('leave');
    closeDrawer();
    goto('path-leave');
  }, [setBranch, closeDrawer, goto]);

  const handleNext = useCallback(() => {
    next();
  }, [next]);

  const handleBackToLobby = useCallback(() => {
    router.push('/');
  }, [router]);

  const handlePlayAgain = useCallback(() => {
    setSkipConfirm(false);
    restart();
  }, [restart]);

  const overlayContent = useMemo(() => {
    if (!mounted) return null;
    return (
      <OverlayShell
        rect={rect}
        dim={state.dim}
        rootRef={setOverlayRoot}
        // Show the spotlight border whenever there's a rect to draw,
        // regardless of `dim`. Steps like `own-tile-blue` set
        // `dim=false` so the board stays bright (the slab needs to
        // read in its natural "owned tile" obsidian-plinth + blue
        // edge ring look) but still want the My Tiles panel marked
        // out — `OverlayShell` then drops the dim mask + force-
        // interaction blockers and just paints the border.
        showSpotlight={!!rect}
        target={state.highlightTarget}
        showSkip={step.id !== 'outro' && !skipConfirm}
        // Outro is the *script-completed* celebration; `skipConfirm`
        // is the *user-initiated* "are you sure" detour. Both render
        // through `CelebrationPanel` so the hero card framing is
        // consistent — the difference is the body content.
        isCelebration={step.id === 'outro' || skipConfirm}
        // Dialogue dock position — `set-price-rent` flips to
        // `near-panel` so the rent beat reads next to the buyout
        // summary it's describing; everything else falls back to
        // bottom-centre per the existing layout. CopyPanel does the
        // fade-swap when this prop changes.
        panelPosition={step.panelPosition ?? 'bottom'}
        onSkip={handleSkip}
      >
        {skipConfirm ? (
          <SkipConfirmBody
            onBackToLobby={handleBackToLobby}
            onPlayAgain={handlePlayAgain}
          />
        ) : (
          <StepBody
            stepId={step.id}
            title={step.title}
            body={step.body}
            cta={step.cta}
            advance={step.advance}
            branch={state.branch}
            onNext={handleNext}
            onDefend={handleBranchDefend}
            onLeave={handleBranchLeave}
            onBackToLobby={handleBackToLobby}
            onPlayAgain={handlePlayAgain}
          />
        )}
      </OverlayShell>
    );
  }, [
    mounted,
    rect,
    state.dim,
    state.highlightTarget,
    state.branch,
    step.id,
    step.title,
    step.body,
    step.cta,
    step.advance,
    step.panelPosition,
    skipConfirm,
    handleSkip,
    handleNext,
    handleBranchDefend,
    handleBranchLeave,
    handleBackToLobby,
    handlePlayAgain,
    setOverlayRoot,
  ]);

  if (!mounted) return null;
  return createPortal(overlayContent, document.body);
}

interface OverlayShellProps {
  rect: SpotlightRect | null;
  dim: boolean;
  showSpotlight: boolean;
  target: string | null;
  showSkip: boolean;
  isCelebration?: boolean;
  /** Where the dialogue dock sits for the current step (defaults to
   *  `'bottom'`). Wired through to `CopyPanel`, which fades + swaps
   *  position whenever this changes so the dialogue glides between
   *  anchors instead of jumping. */
  panelPosition?: DialoguePosition;
  onSkip: () => void;
  /** Setter for the outermost portal `<div>` so the parent
   *  `TutorialOverlay` can hand it to `useTutorialInputGuard` for
   *  containment checks (events targeted inside the portal pass
   *  through the guard). Using a callback ref instead of `forwardRef`
   *  avoids changing the component contract for downstream wrappers
   *  that still treat `OverlayShell` as a plain function component. */
  rootRef?: (node: HTMLDivElement | null) => void;
  children: React.ReactNode;
}

function OverlayShell({
  rect,
  dim,
  showSpotlight,
  target,
  showSkip,
  isCelebration = false,
  panelPosition = 'bottom',
  onSkip,
  rootRef,
  children,
}: OverlayShellProps) {
  // Tight outset so the cutout hugs the panel chrome rather than
  // floating ~12 px away from it (user feedback: "blue highlight
  // boxes are misaligned to their panels — they should neatly
  // wrap/hug the panel"). 4 px gives the dim mask just enough
  // breathing room to read as a separate element from the panel
  // border without painting over the panel's 1 px outline. Paired
  // with the `borderRadius: 10px` cutout below — radius = panel
  // border-radius (6 px on the AdapterStack / OwnedTilesBar /
  // RewardsPanel chrome) + padding (4 px) keeps the cutout's curve
  // parallel to the panel's own corners so the hug is uniform on
  // every side instead of pulling tighter at the corners.
  const padding = 4;
  const ringStyle = rect
    ? {
        top: rect.top - padding,
        left: rect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      }
    : undefined;

  // When a spotlight rect exists we rely on its outset `box-shadow` to
  // dim everything *outside* the highlighted element. The flat dim
  // layer paints when the script wants to mute the whole HUD without
  // a specific cutout (rect-less steps).
  //
  // `dim` is independent of `showSpotlight` now — a step can ask for
  // the rect border to paint *without* dimming the rest of the
  // viewport (`own-tile-blue` does this so the freshly-acquired
  // slab keeps its normal brightness). When `dim=false`:
  //   - showFlatDim is off (we don't want a full-screen dim)
  //   - the spotlight box-shadow drops its outer dim layer (only
  //     the blue ring + glow paints — see `boxShadow` ternary below)
  //   - the four-rect blockers are skipped (no force-interaction
  //     when nothing is being darkened)
  //
  // 3D tile targets now resolve to the projected-AABB marker rect
  // (see `useSpotlightRect`), so the spotlight does paint and
  // `showFlatDim` is `false` for them too — the cutout's box-shadow
  // is the dim mask. The cutout itself is rendered circular (50%
  // border-radius) on 3D tile fallbacks so the bright area matches
  // the in-canvas blue ring shape rather than reading as a
  // misaligned rectangle.
  const showFlatDim = dim && !showSpotlight;

  // Fade-out → swap → fade-in transition between targets. `displayedRect`
  // holds the last visible rect so the spotlight stays mounted at the
  // previous spot while it fades out, then re-mounts at the new spot
  // and fades back in. Avoids the "blue square slides across the
  // screen" visual the user flagged.
  const FADE_MS = 140;
  const [visible, setVisible] = useState(showSpotlight);
  const [displayedRect, setDisplayedRect] = useState<SpotlightRect | null>(rect);
  const lastTargetRef = useRef<string | null>(target);
  useEffect(() => {
    const targetChanged = lastTargetRef.current !== target;
    lastTargetRef.current = target;
    if (!showSpotlight) {
      setVisible(false);
      const t = window.setTimeout(() => setDisplayedRect(null), FADE_MS);
      return () => window.clearTimeout(t);
    }
    if (!targetChanged) {
      setDisplayedRect(rect);
      setVisible(true);
      return;
    }
    setVisible(false);
    const t = window.setTimeout(() => {
      setDisplayedRect(rect);
      // Two RAFs so the new rect lays out before we transition opacity
      // back up — without this React batches both writes into the same
      // paint and the fade-in skips.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setVisible(true));
      });
    }, FADE_MS);
    return () => window.clearTimeout(t);
  }, [showSpotlight, target, rect]);

  // Once visible, follow rect changes from the same target (e.g. window
  // resize, tile camera drift) without re-fading.
  useEffect(() => {
    if (!visible) return;
    setDisplayedRect(rect);
  }, [rect, visible]);

  const activeRect = displayedRect ?? rect;
  const activeRingStyle = activeRect
    ? {
        top: activeRect.top - padding,
        left: activeRect.left - padding,
        width: activeRect.width + padding * 2,
        height: activeRect.height + padding * 2,
      }
    : ringStyle;

  // Force-interaction blockers: when the script is highlighting a
  // specific element we want the user to be physically *unable* to
  // click anything else. The dim mask is purely cosmetic (it doesn't
  // intercept events), so we paint four invisible `pointer-events:auto`
  // rectangles around the spotlight rect that swallow clicks/taps
  // outside the highlighted area. The dialogue panel renders later in
  // the same overlay tree and gets a higher z-index so its buttons
  // still work. Tile targets skip the blockers entirely — the only
  // affordance there is the tile click, and the 3D canvas owns its
  // own input plumbing.
  //
  // Critical: the blockers butt up *directly* against the highlighted
  // rect (no inset padding). The 12 px visual padding is only on the
  // box-shadow ring. If the blockers used `padding` we'd leave a 12 px
  // safe-zone around the rect — and for the 1.2x preset that strip
  // overlaps the 2x / 0.5x adjacent chips because the price-presets
  // grid only has a 6 px gap. The result was the user being able to
  // click 2x even with a "1.2x only" spotlight.
  const swallow = (e: React.SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();
  };
  const blockerCommonClass = 'pointer-events-auto absolute';
  // Force-interaction blockers only fire when the script is
  // actively dimming the rest of the page — `dim=false` steps want
  // the user to be able to use the underlying UI (e.g. once they
  // own the floor, the My Tiles panel and the slab itself are
  // both interactive again). Building the blockers off `dim &&
  // showSpotlight` keeps the previous strict-no-other-clicks
  // behaviour for darkened steps without locking the whole
  // viewport on the post-takeover beat.
  const blockers =
    activeRect && showSpotlight && dim
      ? [
          { key: 'top', top: 0, left: 0, right: 0, height: Math.max(0, activeRect.top) },
          {
            key: 'bottom',
            left: 0,
            right: 0,
            top: activeRect.top + activeRect.height,
            bottom: 0,
          },
          {
            key: 'left',
            top: Math.max(0, activeRect.top),
            left: 0,
            width: Math.max(0, activeRect.left),
            height: activeRect.height,
          },
          {
            key: 'right',
            top: Math.max(0, activeRect.top),
            left: activeRect.left + activeRect.width,
            right: 0,
            height: activeRect.height,
          },
        ]
      : [];

  // Tile targets get a 3D ring as the only highlight in cinematic
  // mode; HUD targets get a visible blue rounded outline + the dim
  // cutout. **Lite (2D) mode** has no in-canvas ring, so 2D tile
  // fallbacks DO get the rounded blue outline — `useSpotlightRect`
  // flags those rects with `is2DTileFallback` so we can branch here.
  // The user feedback was consistent: tiles → ring, panels/buttons →
  // blue rounded square — but Lite tiles need the square as their
  // ring stand-in.
  //
  // Mobile chip-rail pills (`<panel>-pill`) skip the ring entirely
  // (`isPillChip`): the pill already has its own visible affordance
  // (amber border + filled active background), and the user reported
  // the additional blue ring read as visual noise on the tutorial
  // panel-pill steps. The dim mask + spotlight cutout still render,
  // so the pill is still spotlit — only the rounded outline is
  // suppressed. Desktop floating HUD panels (which have no built-in
  // highlighted state) keep the ring on the same script steps because
  // their lookup resolves through the suffix-strip branch where
  // `isPillChip` stays `false`.
  const showVisibleHighlightBorder =
    !!activeRect &&
    !activeRect.isPillChip &&
    (!isTileTarget(target) || !!activeRect.is2DTileFallback);

  return (
    <div ref={rootRef} className="pointer-events-none fixed inset-0 z-[9000]">
      {showFlatDim && (
        // Full-screen dim with the same swallow handlers as the
        // four-rect blockers — covers the case where the script
        // wants to mute the entire HUD (no spotlight target) and
        // also the 1.2 s grace window in `set-price` between the
        // drawer opening and the 1.2x preset getting its highlight.
        // Without `onPointerDownCapture` etc. the user could still
        // tap the 2x / 5x preset chips during the grace window.
        <div
          className="pointer-events-auto absolute inset-0 bg-black/55 transition-opacity duration-300"
          onClick={swallow}
          onClickCapture={swallow}
          onPointerDownCapture={swallow}
          onMouseDownCapture={swallow}
          onTouchStartCapture={swallow}
        />
      )}
      {/* Click blockers around the spotlight — invisible but capture
          every pointer event in the dim region so the user is forced
          to interact with the spotlit element only. */}
      {blockers.map((b) => (
        <div
          key={b.key}
          className={blockerCommonClass}
          style={{
            top: b.top,
            left: b.left,
            right: b.right,
            bottom: b.bottom,
            width: b.width,
            height: b.height,
          }}
          onClickCapture={swallow}
          onPointerDownCapture={swallow}
          onMouseDownCapture={swallow}
          onTouchStartCapture={swallow}
        />
      ))}
      {activeRingStyle && (
        <div
          className="pointer-events-none absolute"
          style={{
            ...activeRingStyle,
            // 3D tile fallbacks render the cutout as a perfect
            // circle so the bright area matches the in-canvas blue
            // ring shape (radius ≈ inscribed circle of the AABB).
            // HUD targets and 2D tile fallbacks use a 10 px cutout
            // radius so the curve sits parallel to the underlying
            // panel chrome (typical 6 px corner + 4 px padding =
            // 10 px) instead of arcing inward toward the panel's
            // content area. With the previous 14 px cutout the
            // dim mask's corner curve clipped *into* the panel
            // because the cutout was rounder than the 12 px
            // outset — exactly the "misaligned" read the user
            // flagged on the yield / rewards / mytiles steps.
            borderRadius: activeRect?.is3DTileFallback ? '50%' : '10px',
            // Multiple box-shadows render *front-to-back*, with the
            // first shadow drawn on TOP. We order the blue ring +
            // glow first (visible), then the giant 9999px dim mask
            // last (drawn underneath). Reversing this order paints
            // the dim mask over the blue ring and the user sees
            // nothing but a dark cutout — which is what they were
            // flagging ("the 1.2x preset isn't highlighted blue").
            //
            // Three-way switch:
            //   • dim + visible border      → border glow + dim mask
            //   • dim + tile target         → dim mask only (3D ring
            //     is the in-canvas highlight)
            //   • !dim + visible border     → border glow only (used
            //     by `own-tile-blue` so the My Tiles panel gets a
            //     callout while the rest of the board stays bright)
            //   • !dim + tile target        → no spotlight
            boxShadow: dim
              ? showVisibleHighlightBorder
                ? '0 0 0 2px rgba(96,165,250,0.95), 0 0 24px rgba(96,165,250,0.55), 0 0 0 9999px rgba(0,0,0,0.55)'
                : '0 0 0 9999px rgba(0,0,0,0.55)'
              : showVisibleHighlightBorder
                ? '0 0 0 2px rgba(96,165,250,0.95), 0 0 24px rgba(96,165,250,0.55)'
                : 'none',
            opacity: visible ? 1 : 0,
            transition: `opacity ${FADE_MS}ms ease`,
          }}
        />
      )}
      {isCelebration ? (
        // Outro takes over the whole viewport with a hero celebration
        // panel — taller, glowier, no skip button. The overlay still
        // sits on top of the dimmed board because the celebration
        // isn't supposed to share visual weight with the lobby chrome
        // behind it.
        <CelebrationPanel>{children}</CelebrationPanel>
      ) : (
        <CopyPanel position={panelPosition}>
          {children}
          {showSkip && (
            <button
              type="button"
              onClick={onSkip}
              className="mt-3 inline-flex items-center gap-1 self-end rounded-md border border-amber-100/15 bg-black/40 px-2 py-1 text-[10px] font-mono uppercase tracking-[0.18em] text-amber-100/65 transition hover:border-amber-100/35 hover:text-amber-50"
            >
              Skip tutorial <X className="h-3 w-3" />
            </button>
          )}
        </CopyPanel>
      )}
    </div>
  );
}

interface BranchCardProps {
  icon: React.ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
}

/**
 * Defend / Leave decision card. Visually matches `ViewModePick` (the
 * 3D vs 2D picker on `view-pick`) so the binary choice on
 * `defend-or-leave` reads as a deliberate fork rather than a pair of
 * generic CTAs. Always rendered un-active — the script handles the
 * branch by routing to either `path-defend` or `path-leave` and
 * never returns to this step.
 */
function BranchCard({ icon, label, sub, onClick }: BranchCardProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={false}
      onClick={onClick}
      className="flex flex-col items-start gap-1.5 rounded-[4px] border border-amber-400/15 bg-black/30 px-3 py-2.5 text-left transition hover:border-amber-400/35 hover:bg-amber-400/5"
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-[3px] bg-amber-100/5 text-amber-100/80">
        {icon}
      </span>
      <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-50">
        {label}
      </span>
      <span className="text-[10px] leading-snug text-amber-100/60">{sub}</span>
    </button>
  );
}

/** Tailwind position classes per `DialoguePosition` value. The dialogue
 *  defaults to bottom-centre to keep the rewards / yield columns visible;
 *  `set-price-rent` flips to `near-panel` so the rent beat reads next to
 *  the buyout summary; `holder-chat` and the other side-anchored steps
 *  use `left` / `right` so the dialogue sits on the side opposite the
 *  highlighted panel.
 *
 *  `near-panel`:
 *   - lg+ (drawer is the 440 px right sheet, viewport ≥ 1024 px) →
 *     anchor flush against the drawer's left edge (`right: 464px`,
 *     ≈24 px gap to the drawer), vertically centred against the
 *     summary block. The dialogue is up to 32 rem (512 px) wide, so
 *     the left edge lands ≥48 px in on a 1024 px viewport — it never
 *     clips, even on narrow desktop windows.
 *   - smaller viewports (sm/md, drawer covers ≥55% of the width
 *     once we account for the dialogue + drawer + gaps) → top-centre
 *     so the dialogue clears the drawer entirely. Below sm the drawer
 *     is full-width anyway and top-centre is the only readable spot.
 */
const COPY_PANEL_POSITION_CLASSES: Record<DialoguePosition, string> = {
  bottom: 'left-1/2 bottom-6 -translate-x-1/2',
  center: 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
  left: 'left-6 bottom-6 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2',
  right: 'right-6 bottom-6 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2',
  // Drawer-hugging position. Mobile + tablet (< lg) keeps the dialogue
  // at the top of the screen so the right-side drawer doesn't squeeze
  // it off-screen. Desktop (lg+) flushes the dialogue against the
  // drawer's left edge — `right-[464px]` puts the dialogue's right
  // edge ~24 px from the drawer (drawer footprint = 440 px max-width
  // + ≈24 px right gutter), vertically centred against the summary.
  'near-panel':
    'left-1/2 top-4 -translate-x-1/2 lg:left-auto lg:top-1/2 lg:translate-x-0 lg:-translate-y-1/2 lg:right-[464px]',
};

/**
 * Dialogue dock for non-celebratory steps.
 *
 * Switches between named anchor positions per `DialoguePosition`. When
 * the position changes we fade the panel out, swap the anchor class,
 * then fade back in — the alternative (animating `top` / `left`
 * directly with `transition-all`) breaks because Tailwind's anchor
 * classes flip between properties (`bottom: 24px` → `auto`, `top:
 * auto` → `50%`) and browsers can't tween between named lengths and
 * `auto`. The fade-swap is short enough to feel like a glide while
 * sidestepping that limitation entirely.
 *
 * Two-state lag pattern:
 *   - `position` is the source-of-truth prop from the script.
 *   - `committed` is the position class currently on screen. Lags
 *     `position` by `SWAP_MS` ms so the swap happens at peak
 *     invisibility (opacity 0).
 *   - `opacity` is 0 during the swap window, 1 otherwise. The CSS
 *     `transition: opacity` handles the actual fade.
 *
 * Edge cases:
 *   - If `position` flips back to the current committed value while a
 *     swap is already in flight (e.g. a quick step skip), we cancel
 *     the pending timer and snap opacity back to 1 — without that
 *     guard, a position bounce-back would leave the panel stuck at
 *     opacity 0 forever (timer cleared, no fresh swap to schedule).
 */
function CopyPanel({
  position = 'bottom',
  children,
}: {
  position?: DialoguePosition;
  children: React.ReactNode;
}) {
  const SWAP_MS = 220;
  const [committed, setCommitted] = useState<DialoguePosition>(position);
  const [opacity, setOpacity] = useState<number>(1);
  useEffect(() => {
    if (position === committed) {
      // No swap needed — and crucially, force opacity back to 1 in
      // case a previous swap-in-flight was cancelled by `position`
      // bouncing back to `committed` before its timer fired.
      setOpacity(1);
      return;
    }
    setOpacity(0);
    const t = window.setTimeout(() => {
      // Commit + restore opacity in the same callback. React batches
      // both state updates into one render commit with the new class
      // and `opacity: 1` — the browser then transitions from the
      // already-painted `opacity: 0` state to `opacity: 1` over
      // `SWAP_MS`, while the new position class applies instantly
      // (no `transition: top/left/...` declared, only opacity).
      // Result: panel fades out at the old position, swaps class
      // while invisible, fades back in at the new position.
      setCommitted(position);
      setOpacity(1);
    }, SWAP_MS);
    return () => window.clearTimeout(t);
  }, [position, committed]);

  const positionClass = COPY_PANEL_POSITION_CLASSES[committed];
  return (
    <div
      className={`pointer-events-auto absolute flex w-[min(94vw,32rem)] flex-col gap-3 rounded-2xl border border-amber-300/25 bg-black/85 p-5 text-amber-50 shadow-[0_18px_48px_rgba(0,0,0,0.55)] backdrop-blur-md ${positionClass}`}
      style={{
        opacity,
        transition: `opacity ${SWAP_MS}ms ease`,
      }}
    >
      {children}
    </div>
  );
}

/**
 * Hero panel for the tutorial outro. Centred in the viewport, taller
 * + wider than the regular dialogue, with a dimmed backdrop to push
 * the lobby chrome behind a smoked-glass layer. The amber gradient
 * + glow ring is meant to feel earned — the user just finished the
 * full takeover loop and the celebration should land like a reward
 * screen rather than another tutorial beat.
 */
function CelebrationPanel({ children }: { children: React.ReactNode }) {
  return (
    <>
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/75 to-black/85 backdrop-blur-sm" />
      <div className="pointer-events-auto absolute left-1/2 top-1/2 flex w-[min(94vw,38rem)] -translate-x-1/2 -translate-y-1/2 flex-col gap-5 overflow-hidden rounded-3xl border border-amber-300/40 bg-gradient-to-br from-amber-500/15 via-black/90 to-black/95 p-8 text-amber-50 shadow-[0_30px_80px_rgba(255,180,40,0.25),0_0_0_1px_rgba(255,210,120,0.18)_inset] backdrop-blur-xl">
        {/* Soft radial glow behind the content — anchored top-right so
            it reads as light pouring in rather than a flat tint. */}
        <div
          aria-hidden
          className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-amber-300/25 blur-3xl"
        />
        <div className="relative flex flex-col gap-5">{children}</div>
      </div>
    </>
  );
}

interface StepBodyProps {
  stepId: TutorialStepId;
  title?: string;
  body: string;
  cta?: string;
  advance: 'manual' | 'auto' | 'event' | 'branch';
  branch: 'defend' | 'leave' | null;
  onNext: () => void;
  onDefend: () => void;
  onLeave: () => void;
  onBackToLobby: () => void;
  onPlayAgain: () => void;
}

function StepBody({
  stepId,
  title,
  body,
  cta,
  advance,
  branch,
  onNext,
  onDefend,
  onLeave,
  onBackToLobby,
  onPlayAgain,
}: StepBodyProps) {
  // `playTutorialTypeSound` is the polished tick used by the /tutorial
  // route — a high-passed click + tiny ping with stereo pan that
  // suggests a caret moving across the line. The legacy
  // `playTutorialTalkSound` (squarewave chirp) stays in place for the
  // pre-grad mascot overlay; this dialogue is meant to feel like the
  // rest of the polished app.
  const { displayed, isDone, skipToEnd } = useTypewriter(body, {
    speed: 18,
    enableSound: true,
    tickSound: playTutorialTypeSound,
  });
  const showViewPick = stepId === 'view-pick';
  const isOutro = stepId === 'outro';

  // Wrap each user-facing button so the polished `hqPlaySeatClick`
  // chimes alongside the navigation/branch handler. This is the same
  // wood-tick + body + resonance sound the launch wizard's prev/next
  // RpgButtons play (the user asked for "the much nicer 3D sound for
  // clicking the next buttons previously … on the create board flow"),
  // so the tutorial's nav buttons now read as part of the same polished
  // app rather than the standalone bell of `playTutorialAdvanceSound`.
  // `ensureAudioReady` + `ensureHqAudioReady` both warm their respective
  // contexts on first interaction (mobile/Chrome need a user gesture
  // before sounds will play; the typewriter context and the HQ pack
  // run on different graphs so we prime both).
  const withClick = (handler: () => void) => () => {
    ensureAudioReady();
    ensureHqAudioReady();
    hqPlaySeatClick();
    handler();
  };

  if (isOutro) {
    // Branch-aware celebration. Copy mirrors the latest script:
    //   - Defend (11.a): "Congratulations, you kept hold of your land
    //     to fight another day. Play again or go to the lobby and
    //     discover live boards."
    //   - Exit   (11.b): "Congratulations, you made your exit and
    //     claimed rewards along the way. Remember, land is always
    //     for sale so you can always come back."
    // Headlines stay short and celebratory so the page reads as a
    // reward screen. CTAs are identical on both branches so muscle-
    // memory doesn't shift between runs.
    const isDefend = branch === 'defend';
    const headline = isDefend
      ? 'You kept hold of your land'
      : 'You made your exit';
    const subtitle = isDefend
      ? 'Congratulations, you kept hold of your land to fight another day. Play again or go to the lobby and discover live boards.'
      : 'Congratulations, you made your exit and claimed rewards along the way. Remember, land is always for sale so you can always come back.';
    return (
      <div className="flex flex-col gap-5">
        <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300/90">
          <span className="inline-block h-px w-6 bg-amber-300/60" />
          Tutorial complete
        </div>
        <div className="text-[28px] font-semibold leading-tight tracking-tight text-amber-50">
          {headline}
        </div>
        <div className="text-[15px] leading-relaxed text-amber-100/80">
          {subtitle}
        </div>
        <div className="mt-2 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            onClick={withClick(onBackToLobby)}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-amber-300/50 bg-amber-300/20 px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] text-amber-50 shadow-[0_8px_24px_rgba(255,180,40,0.25)] transition hover:bg-amber-300/30"
          >
            <ArrowLeftCircle className="h-4 w-4" /> Take me to the lobby
          </button>
          <button
            type="button"
            onClick={withClick(onPlayAgain)}
            className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-amber-100/25 bg-black/45 px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] text-amber-100/90 transition hover:border-amber-100/45 hover:text-amber-50"
          >
            <Repeat className="h-4 w-4" /> Play again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {title && (
        <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-300/85">
          {title}
        </div>
      )}
      {/* Block-level button so the typewriter wraps inside a fixed-width
          column (no horizontal jitter as characters print). The container
          height grows naturally as new lines appear. */}
      <button
        type="button"
        onClick={withClick(skipToEnd)}
        className="block w-full text-left text-[15px] leading-snug text-amber-50/95"
      >
        <span className="block whitespace-pre-wrap break-words">
          {displayed}
          {!isDone && <span className="ml-1 inline-block animate-pulse">▍</span>}
        </span>
      </button>

      {showViewPick && <ViewModePick />}

      {advance === 'branch' ? (
        // Two side-by-side cards styled like `ViewModePick` (the 3D vs
        // 2D picker on `view-pick`) so the defend/leave decision reads
        // as a real, weighty choice rather than a pair of generic
        // buttons. Cards stay roughly the same height so the panel
        // doesn't jump as the typewriter reveals.
        <div role="radiogroup" aria-label="Defend or leave the tile" className="mt-2 grid grid-cols-2 gap-2">
          <BranchCard
            icon={<Shield className="h-4 w-4" />}
            label="Defend"
            sub="Raise your price · keep earning"
            onClick={withClick(onDefend)}
          />
          <BranchCard
            icon={<Flag className="h-4 w-4" />}
            label="Exit"
            sub="Hold price · pocket the buyout"
            onClick={withClick(onLeave)}
          />
        </div>
      ) : advance === 'event' ? (
        <div className="mt-2 inline-flex items-center gap-2 self-start font-mono text-[11px] uppercase tracking-[0.18em] text-amber-200/70">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-300/80" />
          Waiting for you...
        </div>
      ) : advance === 'auto' ? (
        <div className="mt-2 inline-flex items-center gap-2 self-start font-mono text-[11px] uppercase tracking-[0.18em] text-amber-200/70">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-300/80" />
          Watch the board...
        </div>
      ) : (
        <button
          type="button"
          onClick={withClick(onNext)}
          className="mt-2 inline-flex items-center justify-center gap-2 self-start rounded-md border border-amber-300/40 bg-amber-300/15 px-3 py-2 font-mono text-xs uppercase tracking-[0.18em] text-amber-50 transition hover:bg-amber-300/25"
        >
          {cta ?? 'Next'} <ChevronRight className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}

interface SkipConfirmBodyProps {
  onBackToLobby: () => void;
  onPlayAgain: () => void;
}

/**
 * Skip-tutorial confirmation. Replaces `StepBody` inside the
 * `CelebrationPanel` hero card whenever the user clicks the
 * "Skip tutorial" affordance — visually identical to the
 * outro card so the two end-of-flow surfaces share a vocabulary,
 * but with the question + recovery options the user spec'd
 * ("Are you sure you want to leave?" / [Take me to the lobby]
 * / [Play again]) instead of the celebration headline.
 *
 * The two main CTAs reuse the same `onBackToLobby` / `onPlayAgain`
 * handlers as the script-completed outro, so the destination is
 * consistent regardless of how the user reaches "I'm done with
 * the tutorial". There is intentionally no inline cancel — a
 * mis-tap is recovered by clicking "Play again", which deposits
 * the user back at the first step. The user spec was explicit
 * about the two-button layout.
 */
function SkipConfirmBody({
  onBackToLobby,
  onPlayAgain,
}: SkipConfirmBodyProps) {
  const withClick = (handler: () => void) => () => {
    ensureAudioReady();
    ensureHqAudioReady();
    hqPlaySeatClick();
    handler();
  };
  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.32em] text-amber-300/90">
        <span className="inline-block h-px w-6 bg-amber-300/60" />
        Skip tutorial
      </div>
      <div className="text-[28px] font-semibold leading-tight tracking-tight text-amber-50">
        Are you sure you want to leave?
      </div>
      <div className="mt-2 flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={withClick(onBackToLobby)}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-amber-300/50 bg-amber-300/20 px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] text-amber-50 shadow-[0_8px_24px_rgba(255,180,40,0.25)] transition hover:bg-amber-300/30"
        >
          <ArrowLeftCircle className="h-4 w-4" /> Take me to the lobby
        </button>
        <button
          type="button"
          onClick={withClick(onPlayAgain)}
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-amber-100/25 bg-black/45 px-4 py-3 font-mono text-xs uppercase tracking-[0.2em] text-amber-100/90 transition hover:border-amber-100/45 hover:text-amber-50"
        >
          <Repeat className="h-4 w-4" /> Play again
        </button>
      </div>
    </div>
  );
}
